import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "../utils/cn.js";

type Variant = "primary" | "mustard" | "ghost" | "ink";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  fullWidth?: boolean;
}

/*
 * Brand mapping (from app/styles.css `.btn-*`):
 *   primary  = teal fill, cream text       — main CTA
 *   mustard  = mustard fill, ink text      — "Begin today's reset"
 *   ghost    = transparent + strong line   — secondary
 *   ink      = ink fill, cream text        — sidebar Today CTA
 *
 * All buttons are pill-shaped. Active scales to 0.98 per the brand
 * motion rules (no color change on press).
 */
const base =
  "inline-flex items-center justify-center gap-2 rounded-pill font-sans font-semibold " +
  "transition-colors duration-fast ease-soft active:scale-[0.98] " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mustard focus-visible:ring-offset-2 focus-visible:ring-offset-cream " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

const variants: Record<Variant, string> = {
  primary: "bg-teal text-cream hover:bg-teal-2",
  mustard: "bg-mustard text-ink hover:bg-mustard-2",
  ghost:   "bg-transparent text-ink border border-line-strong hover:bg-ink/[0.04]",
  ink:     "bg-ink text-cream hover:bg-ink-2",
};

const sizes: Record<Size, string> = {
  sm: "h-9 px-4 text-small",
  md: "h-11 px-5 text-small",
  lg: "h-14 px-6 text-body",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      leadingIcon,
      trailingIcon,
      fullWidth,
      className,
      children,
      type = "button",
      ...rest
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        base,
        variants[variant],
        sizes[size],
        fullWidth && "w-full",
        className,
      )}
      {...rest}
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </button>
  ),
);
Button.displayName = "Button";
